<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Welcome Header -->
      <div class="text-center sm:text-left">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
          Closing Dashboard
        </h1>
        <p class="text-gray-600 mt-1">Complete each step below to finalize the closing.</p>
      </div>

      <!-- Tolerance Status -->
      <ToleranceBanner :tolerance-status="toleranceStatus" />

      <!-- Vertical Closing Steps -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-gray-900">Closing Steps</h3>
          <p class="text-sm text-gray-500 mt-1">Complete all steps in order to finalize the closing package</p>
        </div>
        <div class="card-body p-0">
          <div class="closing-steps-container">
            <!-- Step 1: Title Info -->
            <div class="closing-step" :class="{ 'step-complete': closingProgress.titleInfoComplete }">
              <div class="step-timeline">
                <div 
                  class="step-circle"
                  :class="getStepCircleClass(closingProgress.titleInfoComplete, 1)"
                >
                  <font-awesome-icon 
                    v-if="closingProgress.titleInfoComplete" 
                    icon="check" 
                    class="text-white text-sm" 
                  />
                  <span v-else class="text-sm font-bold">1</span>
                </div>
                <div class="step-line" :class="{ 'line-complete': closingProgress.titleInfoComplete }"></div>
              </div>
              <router-link 
                to="/title-info"
                class="step-content group"
                :class="closingProgress.titleInfoComplete ? 'bg-success-50 hover:bg-success-100' : 'bg-gray-50 hover:bg-gray-100'"
              >
                <div class="step-icon" :class="closingProgress.titleInfoComplete ? 'bg-success-100' : 'bg-primary-100'">
                  <font-awesome-icon 
                    icon="gavel" 
                    :class="closingProgress.titleInfoComplete ? 'text-success-600' : 'text-primary-600'" 
                  />
                </div>
                <div class="step-info">
                  <h4 class="font-semibold" :class="closingProgress.titleInfoComplete ? 'text-success-800' : 'text-gray-900'">
                    Title Info
                  </h4>
                  <p class="text-sm" :class="closingProgress.titleInfoComplete ? 'text-success-600' : 'text-gray-500'">
                    {{ closingProgress.titleInfoComplete ? 'Completed' : 'Vesting & Legal Description' }}
                  </p>
                </div>
                <div class="step-arrow">
                  <font-awesome-icon 
                    :icon="closingProgress.titleInfoComplete ? 'check-circle' : 'arrow-right'" 
                    :class="closingProgress.titleInfoComplete ? 'text-success-600' : 'text-gray-400 group-hover:text-primary-600'"
                  />
                </div>
              </router-link>
            </div>

            <!-- Step 2: Fees -->
            <div class="closing-step" :class="{ 'step-complete': closingProgress.feesComplete }">
              <div class="step-timeline">
                <div 
                  class="step-circle"
                  :class="getStepCircleClass(closingProgress.feesComplete, 2)"
                >
                  <font-awesome-icon 
                    v-if="closingProgress.feesComplete" 
                    icon="check" 
                    class="text-white text-sm" 
                  />
                  <span v-else class="text-sm font-bold">2</span>
                </div>
                <div class="step-line" :class="{ 'line-complete': closingProgress.feesComplete }"></div>
              </div>
              <router-link 
                to="/fees"
                class="step-content group"
                :class="closingProgress.feesComplete ? 'bg-success-50 hover:bg-success-100' : 'bg-gray-50 hover:bg-gray-100'"
              >
                <div class="step-icon" :class="closingProgress.feesComplete ? 'bg-success-100' : 'bg-primary-100'">
                  <font-awesome-icon 
                    icon="file-invoice-dollar" 
                    :class="closingProgress.feesComplete ? 'text-success-600' : 'text-primary-600'" 
                  />
                </div>
                <div class="step-info">
                  <h4 class="font-semibold" :class="closingProgress.feesComplete ? 'text-success-800' : 'text-gray-900'">
                    Enter Fees
                  </h4>
                  <p class="text-sm" :class="closingProgress.feesComplete ? 'text-success-600' : 'text-gray-500'">
                    {{ closingProgress.feesComplete ? 'Completed' : 'Title & Recording Fees' }}
                  </p>
                </div>
                <div class="step-arrow">
                  <font-awesome-icon 
                    :icon="closingProgress.feesComplete ? 'check-circle' : 'arrow-right'" 
                    :class="closingProgress.feesComplete ? 'text-success-600' : 'text-gray-400 group-hover:text-primary-600'"
                  />
                </div>
              </router-link>
            </div>

            <!-- Step 3: Payoffs -->
            <div class="closing-step" :class="{ 'step-complete': closingProgress.payoffsComplete }">
              <div class="step-timeline">
                <div 
                  class="step-circle"
                  :class="getStepCircleClass(closingProgress.payoffsComplete, 3)"
                >
                  <font-awesome-icon 
                    v-if="closingProgress.payoffsComplete" 
                    icon="check" 
                    class="text-white text-sm" 
                  />
                  <span v-else class="text-sm font-bold">3</span>
                </div>
                <div class="step-line" :class="{ 'line-complete': closingProgress.payoffsComplete }"></div>
              </div>
              <router-link 
                to="/payoffs"
                class="step-content group"
                :class="closingProgress.payoffsComplete ? 'bg-success-50 hover:bg-success-100' : 'bg-gray-50 hover:bg-gray-100'"
              >
                <div class="step-icon" :class="closingProgress.payoffsComplete ? 'bg-success-100' : 'bg-primary-100'">
                  <font-awesome-icon 
                    icon="dollar-sign" 
                    :class="closingProgress.payoffsComplete ? 'text-success-600' : 'text-primary-600'" 
                  />
                </div>
                <div class="step-info">
                  <h4 class="font-semibold" :class="closingProgress.payoffsComplete ? 'text-success-800' : 'text-gray-900'">
                    Payoffs
                  </h4>
                  <p class="text-sm" :class="closingProgress.payoffsComplete ? 'text-success-600' : 'text-gray-500'">
                    {{ closingProgress.payoffsComplete ? 'Completed' : 'Update Payoff Amounts' }}
                  </p>
                </div>
                <div class="step-arrow">
                  <font-awesome-icon 
                    :icon="closingProgress.payoffsComplete ? 'check-circle' : 'arrow-right'" 
                    :class="closingProgress.payoffsComplete ? 'text-success-600' : 'text-gray-400 group-hover:text-primary-600'"
                  />
                </div>
              </router-link>
            </div>

            <!-- Step 4: Preview -->
            <div class="closing-step" :class="{ 'step-complete': closingProgress.previewGenerated }">
              <div class="step-timeline">
                <div 
                  class="step-circle"
                  :class="getStepCircleClass(closingProgress.previewGenerated, 4)"
                >
                  <font-awesome-icon 
                    v-if="closingProgress.previewGenerated" 
                    icon="check" 
                    class="text-white text-sm" 
                  />
                  <span v-else class="text-sm font-bold">4</span>
                </div>
                <div class="step-line" :class="{ 'line-complete': closingProgress.previewGenerated }"></div>
              </div>
              <router-link 
                to="/preview"
                class="step-content group"
                :class="closingProgress.previewGenerated ? 'bg-success-50 hover:bg-success-100' : 'bg-gray-50 hover:bg-gray-100'"
              >
                <div class="step-icon" :class="closingProgress.previewGenerated ? 'bg-success-100' : 'bg-primary-100'">
                  <font-awesome-icon 
                    icon="eye" 
                    :class="closingProgress.previewGenerated ? 'text-success-600' : 'text-primary-600'" 
                  />
                </div>
                <div class="step-info">
                  <h4 class="font-semibold" :class="closingProgress.previewGenerated ? 'text-success-800' : 'text-gray-900'">
                    Preview
                  </h4>
                  <p class="text-sm" :class="closingProgress.previewGenerated ? 'text-success-600' : 'text-gray-500'">
                    {{ closingProgress.previewGenerated ? 'Completed' : 'Review HUD-1 & Instruments' }}
                  </p>
                </div>
                <div class="step-arrow">
                  <font-awesome-icon 
                    :icon="closingProgress.previewGenerated ? 'check-circle' : 'arrow-right'" 
                    :class="closingProgress.previewGenerated ? 'text-success-600' : 'text-gray-400 group-hover:text-primary-600'"
                  />
                </div>
              </router-link>
            </div>

            <!-- Step 5: Generate Documents -->
            <div class="closing-step last-step" :class="{ 'step-complete': closingProgress.documentsGenerated }">
              <div class="step-timeline">
                <div 
                  class="step-circle"
                  :class="getStepCircleClass(closingProgress.documentsGenerated, 5)"
                >
                  <font-awesome-icon 
                    v-if="closingProgress.documentsGenerated" 
                    icon="check" 
                    class="text-white text-sm" 
                  />
                  <span v-else class="text-sm font-bold">5</span>
                </div>
              </div>
              <router-link 
                to="/documents"
                class="step-content group"
                :class="closingProgress.documentsGenerated ? 'bg-success-50 hover:bg-success-100' : 'bg-gray-50 hover:bg-gray-100'"
              >
                <div class="step-icon" :class="closingProgress.documentsGenerated ? 'bg-success-100' : 'bg-primary-100'">
                  <font-awesome-icon 
                    icon="file-contract" 
                    :class="closingProgress.documentsGenerated ? 'text-success-600' : 'text-primary-600'" 
                  />
                </div>
                <div class="step-info">
                  <h4 class="font-semibold" :class="closingProgress.documentsGenerated ? 'text-success-800' : 'text-gray-900'">
                    Generate Documents
                  </h4>
                  <p class="text-sm" :class="closingProgress.documentsGenerated ? 'text-success-600' : 'text-gray-500'">
                    {{ closingProgress.documentsGenerated ? 'Documents Generated' : 'Final Closing Package' }}
                  </p>
                </div>
                <div class="step-arrow">
                  <font-awesome-icon 
                    :icon="closingProgress.documentsGenerated ? 'check-circle' : 'arrow-right'" 
                    :class="closingProgress.documentsGenerated ? 'text-success-600' : 'text-gray-400 group-hover:text-primary-600'"
                  />
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>

    </div>
  </TitleLayout>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import ToleranceBanner from '@/components/ToleranceBanner/index.vue'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Dashboard',
  components: {
    TitleLayout,
    ToleranceBanner,
  },
  setup() {
    const store = useStore()

    const loan = computed(() => store.getters.loan)
    const loanOfficer = computed(() => store.getters.loanOfficer)
    const toleranceStatus = computed(() => store.getters.toleranceStatus)
    const closingProgress = computed(() => store.getters.closingProgress)

    // Get the appropriate class for step circles
    const getStepCircleClass = (isComplete, stepNumber) => {
      const currentStep = getCurrentStep()
      
      if (isComplete) {
        return 'bg-success-600 text-white'
      }
      if (stepNumber === currentStep) {
        return 'bg-primary-600 text-white ring-4 ring-primary-200'
      }
      return 'bg-gray-300 text-gray-600'
    }

    // Determine the current active step
    const getCurrentStep = () => {
      const progress = closingProgress.value
      if (!progress.titleInfoComplete) return 1
      if (!progress.feesComplete) return 2
      if (!progress.payoffsComplete) return 3
      if (!progress.previewGenerated) return 4
      if (!progress.documentsGenerated) return 5
      return 6 // All complete
    }

    const fetchToleranceStatus = async () => {
      try {
        const data = await apiService.getToleranceStatus()
        console.log('[Dashboard] Tolerance status fetched:', data)
        store.dispatch('setToleranceStatus', data)
      } catch (err) {
        console.error('[Dashboard] Failed to fetch tolerance status:', err)
      }
    }

    const fetchLoanOfficer = async () => {
      if (loanOfficer.value) return

      try {
        const data = await apiService.getLoanOfficer()
        console.log('[Dashboard] Loan officer fetched:', data)
        store.dispatch('setLoanOfficer', data)
      } catch (err) {
        console.error('[Dashboard] Failed to fetch loan officer:', err)
      }
    }

    onMounted(() => {
      console.log('[Dashboard] Component mounted, current state:', {
        loan: loan.value,
        closingProgress: closingProgress.value
      })
      fetchToleranceStatus()
      fetchLoanOfficer()
    })

    return {
      loan,
      loanOfficer,
      toleranceStatus,
      closingProgress,
      getStepCircleClass,
    }
  },
}
</script>

<style scoped>
/* Vertical closing steps */
.closing-steps-container {
  @apply py-4;
}

.closing-step {
  @apply flex items-stretch px-6;
}

.step-timeline {
  @apply flex flex-col items-center mr-4;
}

.step-circle {
  @apply w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300;
}

.step-line {
  @apply w-0.5 flex-1 bg-gray-200 my-2 min-h-[20px] transition-colors duration-300;
}

.step-line.line-complete {
  @apply bg-success-600;
}

.last-step .step-timeline {
  @apply items-center;
}

.last-step .step-line {
  @apply hidden;
}

.step-content {
  @apply flex items-center flex-1 p-4 rounded-xl mb-3 transition-all duration-200;
}

.step-icon {
  @apply w-12 h-12 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 transition-colors;
}

.step-info {
  @apply flex-1 min-w-0;
}

.step-arrow {
  @apply ml-3 flex-shrink-0 transition-colors;
}
</style>
